import styled, { css } from 'styled-components';
import { ReactNode } from 'react';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info';
type BadgeSize = 'sm' | 'md';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: ReactNode;
  className?: string;
}

const variantStyles = {
  default: css`
    background: var(--color-border);
    color: var(--color-text-secondary);
  `,
  primary: css`
    background: rgba(81, 20, 97, 0.1);
    color: var(--color-brand-primary);

    [data-theme="dark"] & {
      background: rgba(81, 20, 97, 0.3);
    }
  `,
  success: css`
    background: rgba(52, 199, 89, 0.1);
    color: var(--color-success);

    [data-theme="dark"] & {
      background: rgba(52, 199, 89, 0.2);
    }
  `,
  warning: css`
    background: rgba(255, 149, 0, 0.1);
    color: var(--color-warning);

    [data-theme="dark"] & {
      background: rgba(255, 149, 0, 0.2);
    }
  `,
  error: css`
    background: rgba(255, 59, 48, 0.1);
    color: var(--color-error);

    [data-theme="dark"] & {
      background: rgba(255, 59, 48, 0.2);
    }
  `,
  info: css`
    background: rgba(0, 122, 255, 0.1);
    color: var(--color-info);

    [data-theme="dark"] & {
      background: rgba(0, 122, 255, 0.2);
    }
  `,
};

const sizeStyles = {
  sm: css`
    padding: var(--space-1) var(--space-2);
    font-size: var(--text-xs);
    gap: var(--space-1);
  `,
  md: css`
    padding: var(--space-1) var(--space-3);
    font-size: var(--text-sm);
    gap: var(--space-2);
  `,
};

const StyledBadge = styled.span<{
  $variant: BadgeVariant;
  $size: BadgeSize;
}>`
  display: inline-flex;
  align-items: center;
  font-weight: var(--font-medium);
  border-radius: var(--radius-full);
  white-space: nowrap;

  ${props => variantStyles[props.$variant]}
  ${props => sizeStyles[props.$size]}

  svg {
    width: 1em;
    height: 1em;
  }
`;

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon,
  className,
}: BadgeProps) {
  return (
    <StyledBadge $variant={variant} $size={size} className={className}>
      {icon && icon}
      {children}
    </StyledBadge>
  );
}

// Tech stack specific badges
const techColors: Record<string, string> = {
  'React': '#61DAFB',
  'TypeScript': '#3178C6',
  'Swift': '#F05138',
  'SwiftUI': '#F05138',
  'Firebase': '#FFCA28',
  'Vite': '#646CFF',
  'Shopify': '#7AB55C',
  'Node.js': '#339933',
};

const TechBadge = styled(StyledBadge)<{ $color?: string }>`
  ${props => props.$color && css`
    background: ${props.$color}15;
    color: ${props.$color};

    [data-theme="dark"] & {
      background: ${props.$color}30;
    }
  `}
`;

interface TechBadgeProps {
  tech: string;
  size?: BadgeSize;
}

export function TechStackBadge({ tech, size = 'sm' }: TechBadgeProps) {
  const color = techColors[tech];

  return (
    <TechBadge $variant="default" $size={size} $color={color}>
      {tech}
    </TechBadge>
  );
}
